// src/context/CookieConsentContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const CookieContext = createContext();

// Push a Google Consent Mode v2 update. Safe to call before gtag.js loads —
// dataLayer is initialized at the top of public/index.html, and gtag.js
// replays queued consent commands on load.
function pushConsent(granted) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line no-inner-declarations
  function gtag() {
    window.dataLayer.push(arguments);
  }
  const v = granted ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: v,
    ad_user_data: v,
    ad_personalization: v,
    analytics_storage: v,
  });

  // Meta Pixel: grant releases the events held since page load; revoke keeps
  // them held / stops future ones. Mirrors the Google consent update above.
  if (window.__META_PIXEL_ON && typeof window.fbq === "function") {
    window.fbq("consent", granted ? "grant" : "revoke");
  }
}

export function CookieConsentProvider({ children }) {
  // null = not decided yet, true = accepted, false = declined
  const [cookieConsent, setCookieConsent] = useState(null);

  useEffect(() => {
    // 1. Check LocalStorage on load
    const storedConsent = localStorage.getItem("cookieConsent");

    if (storedConsent === "true") {
      setCookieConsent(true);
      pushConsent(true);
    } else if (storedConsent === "false") {
      setCookieConsent(false);
      pushConsent(false);
    } else {
      // Not decided yet — leave Consent Mode v2 defaults in place
      // (default is 'denied' for EU/EEA/UK/CH per public/index.html).
      setCookieConsent(null);
    }
  }, []);

  const updateConsent = (decision) => {
    setCookieConsent(decision);
    localStorage.setItem("cookieConsent", String(decision));
    pushConsent(decision === true);
  };

  return (
    <CookieContext.Provider value={{ cookieConsent, updateConsent }}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieContext);
}
